import {
  USER_REQUESTED, USER_SUCCESS, USER_CLEAR, LOGIN_CLEAR,
  USER_FAIL,
  LOGIN_REQUESTED, LOGIN_SUCCESS, LOGIN_FAIL,
  SIGNUP_CLEAR, SIGNUP_REQUESTED, SIGNUP_SUCCESS, SIGNUP_FAIL,
  BALANCE_SUCCESS,
} from 'reducers/user';
import { NOTIFICATION_LIST_CLEAR } from 'reducers/notifications';
import { PAYMENT_CLEAR } from 'reducers/payments';
import { MUSIC_REQUEST_LIST_CLEAR } from 'reducers/musicRequests';
import { TOKEN_INVALID } from 'reducers/reset-password';
import { isSSR } from 'helpers/view';
import analytics from 'helpers/analytics';
import { AUTH_TOKEN_EXPIRED } from '../constants';

export const REFRESH_TOKEN_KEY = 'refresh-token';

export function setRefreshToken(token) {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function removeRefreshToken() {
  if (!isSSR()) {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
}

export function getUser() {
  return (dispatch, getState, api) => {
    dispatch({ type: USER_REQUESTED });

    return api.get('/account').then((response) => {
      dispatch({
        type: USER_SUCCESS,
        payload: {
          profile: response,
        },
      });
    }, (err) => {
      if (err.body) {
        dispatch({
          type: USER_FAIL,
          payload: err.body.errorCode,
        });
      }

      return new Error(err);
    });
  };
}

export function login({
  username, password, remember, validation,
}) {
  return (dispatch, _, api) => {
    dispatch({ type: LOGIN_REQUESTED });

    return api.post('/auth/signin', {
      username,
      password,
    }, validation ? {
      'g-recaptcha-response': validation,
    } : {}).then((response) => {
      removeRefreshToken();

      api.setAuthToken(response.authToken);

      if (remember) {
        setRefreshToken(response.refreshToken);
      }

      dispatch([{
        type: LOGIN_SUCCESS,
      }, {
        type: USER_SUCCESS,
        payload: {
          profile: {
            username: response.username,
          },
        },
      }]);
    }).catch(({ body }) => {
      let message = 'Oops, something went wrong.';

      if (body) {
        if (body.errorCode === 'BAD_CREDENTIALS') {
          message = 'Username or password is incorrect';
        } else if (body.errorMessage) {
          message = body.errorMessage;
        }
      }

      dispatch({
        type: LOGIN_FAIL,
        payload: {
          message,
          errorCode: body ? body.errorCode : null,
        },
      });
      setImmediate(() => {
        dispatch({ type: LOGIN_CLEAR });
      });
    });
  };
}

export function logout() {
  return (dispatch, _, api) => {
    api.removeAuthToken();
    removeRefreshToken();

    analytics.clearUserId();

    dispatch([{
      type: USER_CLEAR,
    }, {
      type: NOTIFICATION_LIST_CLEAR,
    }, {
      type: MUSIC_REQUEST_LIST_CLEAR,
    }, {
      type: PAYMENT_CLEAR,
    }, {
      type: TOKEN_INVALID,
    }]);
  };
}

export function setAuthToken(authToken) {
  return (_, __, api) => api.setAuthToken(authToken);
}

export function signUp(data, headers) {
  return (dispatch, _, api) => {
    dispatch([{
      type: USER_CLEAR,
    }, {
      type: SIGNUP_REQUESTED,
    }]);

    return api.post('/account', data, headers)
      .then((response) => {
        api.setAuthToken(response.authToken);

        dispatch([{
          type: SIGNUP_SUCCESS,
        }, {
          type: LOGIN_SUCCESS,
        }, {
          type: USER_SUCCESS,
          payload: {
            profile: {
              username: response.username,
              userEmail: data.userEmail,
            },
          },
        }]);
      })
      .catch((e) => {
        dispatch({
          type: SIGNUP_FAIL,
          payload: e,
        });

        return Promise.reject(e);
      });
  };
}

export function clearSignUp() {
  return (dispatch) => dispatch({ type: SIGNUP_CLEAR });
}

export function getProfile() {
  return (dispatch, _, api) => {
    dispatch({ type: USER_REQUESTED });

    return api.get('/account/profile')
      .then((response) => {
        dispatch({
          type: USER_SUCCESS,
          payload: {
            profile: response,
          },
        });
      });
  };
}

export function updateProfile(fiedls) {
  return (dispatch, _, api) => {
    dispatch({ type: USER_REQUESTED });

    return api.put('/account/profile', fiedls)
      .then((response) => {
        dispatch({
          type: USER_SUCCESS,
          payload: {
            profile: response,
          },
        });
      });
  };
}

export function updatePassword(fields) {
  return (__, _, api) => {
    return api.put('/account/password', fields)
      .then((response) => {
        api.setAuthToken(response.authToken);
        removeRefreshToken();

        return response;
      });
  };
}

export function getEmailPrefs() {
  return (dispatch, _, api) => {
    dispatch({ type: USER_REQUESTED });

    return api.get('/account/email-prefs')
      .then((response) => {
        dispatch({
          type: USER_SUCCESS,
          payload: {
            emailPrefs: response,
          },
        });
      });
  };
}

export function updateEmailPrefs(fields) {
  return (dispatch, _, api) => {
    dispatch({ type: USER_REQUESTED });
    return api.put('/account/email-prefs', fields)
      .then((response) => {
        dispatch({
          type: USER_SUCCESS,
          payload: {
            emailPrefs: response,
          },
        });
      });
  };
}

export const getBalance = () => (dispatch, _, api) => {
  return api.get('/balance')
    .then((response) => {
      dispatch({
        type: BALANCE_SUCCESS,
        payload: {
          balance: response.balance,
        },
      });
    });
};

export const refreshAuthToken = () => (dispatch, _, api) => {
  const refreshTokenValue = getRefreshToken();

  if (!refreshTokenValue) {
    return Promise.reject();
  }

  api.removeAuthToken();

  return api.post('/auth/token', { refreshToken: refreshTokenValue })
    .then((response) => {
      api.setAuthToken(response.authToken);
    })
    .catch((err) => {
      // a network error / timeout has no `body` - don't destroy a working session over it
      if (err && err.body) {
        removeRefreshToken();
      }

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({ body: { errorCode: AUTH_TOKEN_EXPIRED } });
    });
};
